import React, { useMemo, useState } from 'react';
import { extensionStatuses } from 'flow/utils';
import ActivityContainer from 'flow/components/pbx/supervisor/sub-components/ActivityContainer';
import AgentQueueStatus from 'flow/components/pbx/supervisor/AgentQueueStatus';
import { TypedMap } from 'flow/types/TypedMap';
import { Statuses } from 'flow/types/Extension';

import { ExtensionKey } from 'flow/types/EntityKeys';
import SimplePopover from 'flow/components/ui-components/SimplePopover';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    getQueueDescriptionFromQueueId,
    getQueueNumberFromQueueId,
} from 'flow/selectors/selectors';
import { ActiveCallDirections } from 'flow/utils/call-utils';
import { selectNextScheduledProfile } from 'flow/selectors/ExtensionSelectors';
import { makeSelectActiveLineData } from 'flow/selectors/SupervisorSelectors';
import { sessionIsNonCallTelavoxTicket } from 'flow/utils/call-utils-TW';

interface Props {
    extensionKey: ExtensionKey;
    profileCurrent: TypedMap<any>;
    agentStatus: Statuses;
    profileEndTime: number;
    useAgentQueueStatusTooltip: boolean;
}

const inCall = (status: string) =>
    status === extensionStatuses.inCall.key ||
    status === extensionStatuses.inExternalCall.key;

const AgentActivityWrapper = ({
    extensionKey,
    profileCurrent,
    agentStatus,
    profileEndTime,
    useAgentQueueStatusTooltip,
}: Props) => {
    //TO DO: Look at this, if there should be some memoization, as it filters

    const selectActiveLineData = useMemo(makeSelectActiveLineData, [
        extensionKey,
    ]);
    const firstItemRingingToAgent = useAppSelector((state) =>
        selectActiveLineData(state, extensionKey)?.first(),
    );

    const queueId = firstItemRingingToAgent?.get('queue');
    const queueDescription = useAppSelector((state) =>
        getQueueDescriptionFromQueueId(state, queueId),
    );
    const queueNumber = useAppSelector((state) =>
        getQueueNumberFromQueueId(state, queueId),
    );

    const via = queueDescription || queueNumber;
    const callNumber =
        firstItemRingingToAgent &&
        (firstItemRingingToAgent.get('direction') ===
        ActiveCallDirections.INCOMING.value
            ? firstItemRingingToAgent.get('source')
            : firstItemRingingToAgent.get('destination'));

    const profileNext = useAppSelector((state) =>
        selectNextScheduledProfile(state, extensionKey),
    );

    const activeCallDirection = firstItemRingingToAgent?.get('direction');

    const nextProfileIsSameAsCurrentProfile =
        profileCurrent &&
        profileNext &&
        profileCurrent.get('description') === profileNext.get('description');
    const hasSoonLoggedInProfile =
        profileNext && profileEndTime && !nextProfileIsSameAsCurrentProfile;
    const activityFirstContainerType = hasSoonLoggedInProfile
        ? 'duo_first'
        : 'single';

    const [firstContainerIsHovered, setFirstContainerIsHovered] =
        useState(false);
    const [secondContainerIsHovered, setSecondContainerIsHovered] =
        useState(false);

    const [firstReferenceElement, setFirstReferenceElement] =
        useState<HTMLElement>();
    const [secondReferenceElement, setSecondReferenceElement] =
        useState<HTMLElement>();

    const agentQueueStatusComponent = (
        <AgentQueueStatus
            extensionKey={extensionKey}
            callNumber={callNumber}
            via={via}
            callStartTime={firstItemRingingToAgent?.get('startTime')}
            callAnsweredTime={firstItemRingingToAgent?.get('answeredTime')}
            inConfirmedCall={inCall(agentStatus)}
        />
    );

    const getActivityFirstContainer = () => {
        let timestamp = undefined;
        if (
            agentStatus === extensionStatuses.inCall.key ||
            agentStatus === extensionStatuses.inExternalCall.key
        ) {
            timestamp = firstItemRingingToAgent?.get('answeredTime');
        } else if (agentStatus === extensionStatuses.ringingCall.key) {
            timestamp = firstItemRingingToAgent?.get('startTime');
        }

        const component = (
            <ActivityContainer
                profile={profileCurrent}
                containerType={activityFirstContainerType}
                status={agentStatus}
                itemRingingIsTicketNonCall={sessionIsNonCallTelavoxTicket(
                    firstItemRingingToAgent,
                )}
                timestamp={timestamp}
                via={via}
                activeCallDirection={activeCallDirection}
            />
        );
        return useAgentQueueStatusTooltip ? (
            <div
                onMouseEnter={() => setFirstContainerIsHovered(true)}
                onMouseLeave={() => setFirstContainerIsHovered(false)}
                ref={setFirstReferenceElement}
                className="max-w-fit"
            >
                {component}
                {firstContainerIsHovered ? (
                    <SimplePopover referenceElement={firstReferenceElement}>
                        {agentQueueStatusComponent}
                    </SimplePopover>
                ) : null}
            </div>
        ) : (
            <div className="max-w-fit">{component}</div>
        );
    };

    const getActivitySecondContainer = () => {
        const queueStatusComponent = (
            <AgentQueueStatus profileNext={profileNext} />
        );
        return (
            <div
                onMouseEnter={() => setSecondContainerIsHovered(true)}
                onMouseLeave={() => setSecondContainerIsHovered(false)}
                ref={setSecondReferenceElement}
                className="max-w-fit"
            >
                <ActivityContainer
                    containerType={'duo_last'}
                    profile={profileNext}
                    timestamp={profileEndTime}
                    status={
                        profileNext.get('available')
                            ? extensionStatuses.available.key
                            : extensionStatuses.unavailable.key
                    }
                />
                {secondContainerIsHovered ? (
                    <SimplePopover referenceElement={secondReferenceElement}>
                        <div className="max-w-fit">{queueStatusComponent}</div>
                    </SimplePopover>
                ) : null}
            </div>
        );
    };

    return (
        <div className="flex w-fit">
            {getActivityFirstContainer()}
            {hasSoonLoggedInProfile && getActivitySecondContainer()}
        </div>
    );
};

export default AgentActivityWrapper;
